import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Image as ImageIcon, X, ChevronLeft, ChevronRight, Download, Trash2, AlertCircle, Eye } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent } from './ui/dialog';
import PhotoInteractions from './PhotoInteractions';
import ClientPhotoViewer from './ClientPhotoViewer';
import { getBackendUrl } from '../utils/getBackendUrl';

const API = `${getBackendUrl()}/api`;

const PhotoGallery = ({ photos, onDelete, canDelete, onSetHero, isAdmin, currentUserId, user }) => {
  const [viewerPhoto, setViewerPhoto] = useState(null);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [loadingStates, setLoadingStates] = useState({});
  const [errorStates, setErrorStates] = useState({});
  const [retryCount, setRetryCount] = useState({});
  // Touch swipe handling for the fullscreen viewer
  const touchStartRef = useRef(null);

  const handleTouchStart = (e) => {
    const t = e.touches?.[0];
    if (!t) return;
    touchStartRef.current = { x: t.clientX, y: t.clientY, time: Date.now() };
  };

  const handleTouchEnd = (e) => {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start) return;
    const t = e.changedTouches?.[0];
    if (!t) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    const elapsed = Date.now() - start.time;
    // Horizontal swipe: distance > 50px, mostly horizontal (|dx| > 2*|dy|), under 600ms
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 2 && elapsed < 600) {
      navigatePhoto(dx < 0 ? 'next' : 'prev');
    }
  };

  // Keyboard arrows for the viewer (desktop / external keyboard)
  useEffect(() => {
    if (!viewerPhoto) return;
    const onKey = (e) => {
      if (e.key === 'ArrowRight') navigatePhoto('next');
      else if (e.key === 'ArrowLeft') navigatePhoto('prev');
      else if (e.key === 'Escape') closeViewer();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line
  }, [viewerPhoto, viewerIndex, photos.length]);

  const openPhotoViewer = (photo, index) => {
    setViewerPhoto(photo);
    setViewerIndex(index);
    // If current user is a client, record the view so admins get a read receipt
    if (user?.role === 'client' && photo?.id) {
      axios.post(`${API}/photos/${photo.id}/view`).catch(() => { /* silent */ });
    }
  };

  const closeViewer = () => {
    setViewerPhoto(null);
  };

  const navigatePhoto = (direction) => {
    const newIndex = direction === 'next' 
      ? (viewerIndex + 1) % photos.length 
      : (viewerIndex - 1 + photos.length) % photos.length;
    
    setViewerIndex(newIndex);
    setViewerPhoto(photos[newIndex]);
    // Record client views on swipe/arrow navigation too
    if (user?.role === 'client' && photos[newIndex]?.id) {
      axios.post(`${API}/photos/${photos[newIndex].id}/view`).catch(() => { /* silent */ });
    }
  };

  const handleImageLoad = (photoId) => {
    setLoadingStates(prev => ({ ...prev, [photoId]: false }));
    setErrorStates(prev => ({ ...prev, [photoId]: false }));
    // Reset retry count on successful load
    setRetryCount(prev => ({ ...prev, [photoId]: 0 }));
  };

  const handleImageError = (photoId, photoPath, imgElement) => {
    const currentRetries = retryCount[photoId] || 0;
    const backendUrl = getBackendUrl();
    
    console.error('[PhotoGallery] Image failed to load:', {
      photoId,
      photoPath,
      fullUrl: `${backendUrl}${photoPath}`,
      currentHostname: window.location.hostname,
      backendUsed: backendUrl,
      retryAttempt: currentRetries
    });

    // Retry up to 2 times with delay
    if (currentRetries < 2 && imgElement) {
      setRetryCount(prev => ({ ...prev, [photoId]: currentRetries + 1 }));
      
      // Retry after a short delay (mobile networks can be slow)
      setTimeout(() => {
        console.log(`[PhotoGallery] Retrying image load for ${photoId}, attempt ${currentRetries + 1}`);
        // Force reload by adding a new timestamp
        const url = getImageUrl({ ...photos.find(p => p.id === photoId), timestamp: Date.now() });
        imgElement.src = url;
      }, 1000 * (currentRetries + 1)); // 1s, 2s delay
    } else {
      // Max retries reached, mark as error
      setLoadingStates(prev => ({ ...prev, [photoId]: false }));
      setErrorStates(prev => ({ ...prev, [photoId]: true }));
    }
  };

  const getImageUrl = (photo) => {
    if (!photo || !photo.file_path) {
      console.error('[PhotoGallery] Invalid photo object:', photo);
      return '';
    }
    
    // Get backend URL dynamically every time
    const backendUrl = getBackendUrl();
    
    // Ensure file_path starts with /api/uploads
    let path = photo.file_path;
    if (!path.startsWith('/api')) {
      path = `/api/uploads/photos/${path}`;
    }
    
    // Add cache-busting for mobile browsers (especially Safari)
    // Use photo timestamp or upload time as version parameter
    const cacheParam = photo.timestamp ? new Date(photo.timestamp).getTime() : Date.now();
    const separator = path.includes('?') ? '&' : '?';
    
    return `${backendUrl}${path}${separator}v=${cacheParam}`;
  };

  const downloadPhoto = (photo) => {
    const url = getImageUrl(photo);
    const link = document.createElement('a');
    link.href = url;
    link.download = `photo-${photo.id}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!photos || photos.length === 0) {
    return (
      <div className="text-center py-12 bg-slate-800/30 rounded-lg border-2 border-dashed border-slate-700">
        <ImageIcon className="w-16 h-16 mx-auto text-slate-600 mb-4" />
        <p className="text-slate-400 text-lg mb-2">No photos yet</p>
        <p className="text-slate-500 text-sm">Upload your first photo to get started</p>
      </div>
    );
  }

  return (
    <>
      {/* Photo Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {photos.map((photo, index) => (
          <div
            key={photo.id}
            className="group relative aspect-square bg-slate-800 rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-blue-500 transition-all"
            onClick={() => openPhotoViewer(photo, index)}
          >
            {/* Loading State */}
            {loadingStates[photo.id] !== false && !errorStates[photo.id] && (
              <div className="absolute inset-0 flex items-center justify-center bg-slate-800">
                <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}

            {/* Error State */}
            {errorStates[photo.id] && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-800 text-slate-400 p-4">
                <AlertCircle className="w-8 h-8 mb-2" />
                <p className="text-xs text-center">Image not available</p>
              </div>
            )}

            {/* Image */}
            <img
              src={getImageUrl(photo)}
              alt={photo.description || 'Project photo'}
              className={`w-full h-full object-cover transition-opacity ${
                loadingStates[photo.id] !== false || errorStates[photo.id] ? 'opacity-0' : 'opacity-100'
              }`}
              loading="lazy"
              onLoad={() => handleImageLoad(photo.id)}
              onError={(e) => handleImageError(photo.id, photo.file_path, e.target)}
            />

            {/* Hover Overlay */}
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
              <ImageIcon className="w-8 h-8 text-white" />
            </div>

            {/* Hero Badge */}
            {photo.is_hero_photo && (
              <div className="absolute top-2 left-2">
                <div className="bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg">
                  ★ HERO
                </div>
              </div>
            )}

            {/* Pending Approval Badge — shown to anyone who isn't a client */}
            {photo.approval_status === 'pending' && (
              <div className="absolute top-2 right-2">
                <div className="bg-amber-500 text-white text-[10px] font-bold px-2 py-1 rounded-full shadow-lg flex items-center gap-1">
                  <span className="inline-block w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
                  PENDING
                </div>
              </div>
            )}
            {photo.approval_status === 'pending' && (
              <div className="absolute inset-0 bg-amber-500/10 pointer-events-none" />
            )}

            {/* Client Read Receipt + view counts — admin only */}
            {isAdmin && Array.isArray(photo.client_views) && photo.client_views.length > 0 && (
              <div
                className="absolute bottom-2 right-2"
                title={photo.client_views
                  .map(v => `${v.user_name} • ${v.view_count || 1} view${(v.view_count || 1) !== 1 ? 's' : ''} • last: ${new Date(v.viewed_at).toLocaleString('en-CA', { timeZone: 'America/Toronto' })}`)
                  .join('\n')
                }
                data-testid={`photo-read-badge-${photo.id}`}
              >
                <div className="bg-emerald-600 text-white text-[10px] font-bold px-2 py-1 rounded-full shadow-lg flex items-center gap-1">
                  <Eye className="w-3 h-3" />
                  {photo.total_views || photo.client_views.reduce((s, v) => s + (v.view_count || 1), 0)} view{(photo.total_views || photo.client_views.length) !== 1 ? 's' : ''}
                </div>
              </div>
            )}

            {/* Description */}
            {photo.description && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-2">
                <p className="text-white text-xs truncate">{photo.description}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Photo Viewer Modal — clients get the fullscreen Facebook-style viewer */}
      {viewerPhoto && user?.role === 'client' ? (
        <ClientPhotoViewer
          photos={photos}
          index={viewerIndex}
          photo={viewerPhoto}
          getImageUrl={getImageUrl}
          onNavigate={navigatePhoto}
          onClose={closeViewer}
        />
      ) : viewerPhoto && (
        <Dialog open={true} onOpenChange={closeViewer}>
          <DialogContent className="max-w-7xl w-full h-[90vh] p-0 bg-black border-none">
            <div
              className="relative w-full h-full flex flex-col touch-pan-y"
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
              data-testid="photo-viewer"
            >
              {/* Header */}
              <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/80 to-transparent p-2 sm:p-4">
                <div className="text-white mb-3">
                  <p className="font-semibold text-base sm:text-lg">{viewerPhoto.description || 'Project Photo'}</p>
                  <p className="text-xs sm:text-sm text-gray-300">By {viewerPhoto.uploaded_by_name}</p>
                  <p className="text-xs text-gray-400">
                    {new Date(viewerPhoto.timestamp).toLocaleString('en-CA', { timeZone: 'America/Toronto' })}
                  </p>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {isAdmin && (
                    <>
                      <Button
                        size="lg"
                        variant="outline"
                        onClick={() => onSetHero && onSetHero(viewerPhoto)}
                        className={`min-h-[44px] px-4 ${
                          viewerPhoto.is_hero_photo 
                            ? 'bg-blue-600 border-blue-600 text-white' 
                            : 'bg-blue-600/20 border-blue-600 text-blue-400'
                        }`}
                      >
                        {viewerPhoto.is_hero_photo ? '★ Hero Photo' : 'Set as Hero'}
                      </Button>
                    </>
                  )}
                  
                  {/* Show delete button if: admin OR user uploaded this photo */}
                  {canDelete && (isAdmin || viewerPhoto.uploaded_by === currentUserId) && (
                    <Button
                      size="lg"
                      variant="outline"
                      onClick={() => onDelete && onDelete(viewerPhoto.id)}
                      className="bg-red-600/20 border-red-600 text-red-400 min-h-[44px] px-4"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                  )}
                  
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => downloadPhoto(viewerPhoto)}
                    className="bg-green-600/20 border-green-600 text-green-400"
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                  
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={closeViewer}
                    className="bg-gray-600/20 border-gray-600 text-gray-400"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Main Image */}
              <div className="flex-1 flex items-center justify-center p-4 pt-20">
                <img
                  src={getImageUrl(viewerPhoto)}
                  alt={viewerPhoto.description || 'Project photo'}
                  className="max-w-full max-h-full object-contain"
                  onError={(e) => {
                    console.error('[PhotoGallery] Viewer image failed to load:', viewerPhoto.file_path);
                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect width="400" height="300" fill="%23374151"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%239CA3AF" font-size="18"%3EImage not available%3C/text%3E%3C/svg%3E';
                  }}
                />
              </div>

              {/* Navigation Arrows */}
              {photos.length > 1 && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigatePhoto('prev');
                    }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigatePhoto('next');
                    }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </>
              )}

              {/* Photo Interactions (Likes & Comments) */}
              {user && (
                <div className="absolute bottom-0 left-0 right-0 bg-white dark:bg-gray-900">
                  <PhotoInteractions
                    photo={viewerPhoto}
                    user={user}
                    onPhotoUpdate={(photoId, updates) => {
                      // Update local photo data
                      const updatedPhotos = photos.map(p =>
                        p.id === photoId ? { ...p, ...updates } : p
                      );
                      // Trigger parent update if needed
                    }}
                  />
                </div>
              )}

              {/* Photo Counter */}
              <div className={`absolute ${user ? 'bottom-32' : 'bottom-4'} left-1/2 -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-full text-sm z-20`}>
                {viewerIndex + 1} / {photos.length}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

// Memoize component to prevent unnecessary re-renders
export default React.memo(PhotoGallery, (prevProps, nextProps) => {
  // Only re-render if photos array actually changed
  return (
    prevProps.photos === nextProps.photos &&
    prevProps.canDelete === nextProps.canDelete &&
    prevProps.isAdmin === nextProps.isAdmin
  );
});
